// Fix CN (2026-09-13): hoja de compras de la obra con imputación retroactiva.
// "Podemos agregar la opción de imputar a ítem y nivel todo lo comprado hasta ahora...
// sería estirar todas las OCs cerradas y que desde ahí se pueda realizar las
// imputaciones, ya sea a Ítem, Nivel o Tarea" + "un resumen de montos Totales de cada
// tipo: Material, Material Menor, Herramienta, Equipamiento, Alquileres, EPIs, Sin
// Asignación" + "contar con el filtro en cada columna" (Edson, 13/09).
import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Save, ShoppingCart, Filter } from "lucide-react";
import { API } from "../../contexts/AuthContext";
import { formatPYG, formatCantidad } from "../../utils/format";

const opts = { withCredentials: true };
const CATEGORIA_LABEL = {
  listado: "Material (del Pto.)", materiales: "Material", menores: "Material Menor",
  imprevistos: "Imprevisto", equipamientos: "Equipamiento", herramientas: "Herramienta",
  epis: "EPI", alquileres: "Alquiler",
};

export default function ComprasImputacionTab({ obraId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cambios, setCambios] = useState({});          // "ocId|idx" -> { item_uid, nivel_id, tarea_numero, categoria }
  const [f, setF] = useState({ material: "", oc: "", proveedor: "", categoria: "", nivel: "", item: "", pendientes: false });

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/obras/${obraId}/compras-lineas`, opts);
      setData(data);
      setCambios({});
    } catch (err) { toast.error(err.response?.data?.detail || "No se pudieron cargar las compras de la obra"); }
    finally { setLoading(false); }
  }, [obraId]);

  useEffect(() => { cargar(); }, [cargar]);

  const setFiltro = (k, v) => setF(prev => ({ ...prev, [k]: v }));
  const claveDe = (l) => `${l.oc_id}|${l.idx}`;
  const valorDe = (l, campo) => {
    const c = cambios[claveDe(l)] || {};
    return c[campo] !== undefined ? c[campo] : (l[campo] ?? "");
  };
  const editar = (l, campo, valor) => setCambios(prev => ({
    ...prev, [claveDe(l)]: { ...(prev[claveDe(l)] || {}), [campo]: valor },
  }));

  const lineas = useMemo(() => {
    const todas = data?.lineas || [];
    const txt = (v, q) => !q || String(v || "").toLowerCase().includes(q.toLowerCase());
    return todas.filter(l =>
      txt(l.material, f.material) && txt(l.oc_number, f.oc) && txt(l.proveedor, f.proveedor)
      && (!f.categoria || (valorDe(l, "categoria") || "") === f.categoria)
      && (!f.nivel || (valorDe(l, "nivel_id") || "") === f.nivel)
      && (!f.item || (valorDe(l, "item_uid") || "") === f.item)
      && (!f.pendientes || !l.imputada));
  }, [data, f, cambios]);   // eslint-disable-line react-hooks/exhaustive-deps

  const totalFiltrado = useMemo(() => lineas.reduce((s, l) => s + (Number(l.total_neto) || 0), 0), [lineas]);

  const guardar = async () => {
    const payload = Object.entries(cambios).map(([k, v]) => {
      const [oc_id, idx] = k.split("|");
      const linea = { oc_id, idx: Number(idx) };
      if (v.item_uid !== undefined) linea.item_uid = v.item_uid || "";
      if (v.nivel_id !== undefined) linea.nivel_id = v.nivel_id || "";
      if (v.categoria !== undefined) linea.categoria = v.categoria || "";
      if (v.tarea_numero !== undefined) linea.tarea_numero = v.tarea_numero === "" ? null : Number(v.tarea_numero);
      return linea;
    });
    if (!payload.length) { toast.error("No hay cambios para guardar"); return; }
    setSaving(true);
    try {
      const { data: r } = await axios.post(`${API}/obras/${obraId}/compras-lineas/imputar`, { lineas: payload }, opts);
      toast.success(`${r.lineas} línea(s) imputada(s) · ${r.ocs} orden(es) · ${r.pedidos} pedido(s) actualizado(s)`);
      cargar();
    } catch (err) { toast.error(err.response?.data?.detail || "No se pudo guardar la imputación"); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-gray-400" /></div>;
  if (!data) return null;
  const pendientes = Object.keys(cambios).length;

  return (
    <div className="space-y-4" data-testid="obra-compras-imputacion">
      {/* Resumen por tipo */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2" data-testid="compras-resumen">
        {(data.resumen || []).map(r => (
          <div key={r.tipo} className={`rounded-xl border p-3 ${r.tipo === "sin_asignacion" && r.lineas ? "border-amber-300 bg-amber-50/60" : "border-gray-200/60 bg-white"}`}
            data-testid={`compras-resumen-${r.tipo}`}>
            <p className="text-[10px] font-semibold text-gray-500 uppercase leading-tight">{r.label}</p>
            <p className="text-sm font-bold text-gray-900 mt-0.5">{formatPYG(r.total_neto)}</p>
            <p className="text-[10px] text-gray-400">{r.lineas} línea(s)</p>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs font-bold text-gray-700 uppercase tracking-wide flex items-center gap-1.5">
          <ShoppingCart size={13} className="text-[#EA580C]" /> Compras de la obra
          <span className="font-normal text-gray-400 normal-case">
            {lineas.length} de {data.lineas.length} línea(s) · {formatPYG(totalFiltrado)} sin IVA
            {data.sin_imputar ? ` · ${data.sin_imputar} sin imputar` : ""}
          </span>
        </p>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1.5 text-xs text-gray-600">
            <input type="checkbox" checked={f.pendientes} onChange={e => setFiltro("pendientes", e.target.checked)} data-testid="compras-filtro-pendientes" />
            Solo sin imputar
          </label>
          <button onClick={guardar} disabled={saving || !pendientes}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#2E6DA4] hover:bg-[#25597F] text-white text-xs font-semibold disabled:opacity-50"
            data-testid="compras-guardar-btn">
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
            Guardar imputación{pendientes ? ` (${pendientes})` : ""}
          </button>
        </div>
      </div>

      <div className="overflow-x-auto bg-white rounded-2xl border border-gray-200/60">
        <table className="w-full text-xs">
          <thead className="bg-gray-50/60 text-gray-400 uppercase">
            <tr>
              <th className="text-left px-3 py-2">OC</th>
              <th className="text-left px-3 py-2">Proveedor</th>
              <th className="text-left px-3 py-2">Material</th>
              <th className="text-right px-3 py-2">Cant.</th>
              <th className="text-right px-3 py-2">P. Unit.</th>
              <th className="text-right px-3 py-2">Total (sin IVA)</th>
              <th className="text-left px-3 py-2">Ítem del Pto.</th>
              <th className="text-left px-3 py-2">Nivel</th>
              <th className="text-left px-3 py-2">Tarea</th>
              <th className="text-left px-3 py-2">Tipo</th>
            </tr>
            <tr className="bg-white">
              <th className="px-2 py-1"><input value={f.oc} onChange={e => setFiltro("oc", e.target.value)} placeholder="Filtrar" className="w-20 border border-gray-200 rounded px-1.5 py-1 font-normal" data-testid="compras-filtro-oc" /></th>
              <th className="px-2 py-1"><input value={f.proveedor} onChange={e => setFiltro("proveedor", e.target.value)} placeholder="Filtrar" className="w-28 border border-gray-200 rounded px-1.5 py-1 font-normal" data-testid="compras-filtro-proveedor" /></th>
              <th className="px-2 py-1"><input value={f.material} onChange={e => setFiltro("material", e.target.value)} placeholder="Filtrar" className="w-40 border border-gray-200 rounded px-1.5 py-1 font-normal" data-testid="compras-filtro-material" /></th>
              <th colSpan={3} className="px-2 py-1 text-right text-[10px] font-normal text-gray-400"><Filter size={10} className="inline" /> filtros por columna</th>
              <th className="px-2 py-1">
                <select value={f.item} onChange={e => setFiltro("item", e.target.value)} className="w-36 border border-gray-200 rounded px-1.5 py-1 font-normal" data-testid="compras-filtro-item">
                  <option value="">Todos</option>
                  {(data.items_pto || []).map(i => <option key={i.item_uid} value={i.item_uid}>{i.numero} {i.descripcion}</option>)}
                </select>
              </th>
              <th className="px-2 py-1">
                <select value={f.nivel} onChange={e => setFiltro("nivel", e.target.value)} className="w-24 border border-gray-200 rounded px-1.5 py-1 font-normal" data-testid="compras-filtro-nivel">
                  <option value="">Todos</option>
                  {(data.niveles || []).map(n => <option key={n.id} value={n.id}>{n.label}</option>)}
                </select>
              </th>
              <th className="px-2 py-1"></th>
              <th className="px-2 py-1">
                <select value={f.categoria} onChange={e => setFiltro("categoria", e.target.value)} className="w-28 border border-gray-200 rounded px-1.5 py-1 font-normal" data-testid="compras-filtro-categoria">
                  <option value="">Todos</option>
                  {(data.categorias || []).map(c => <option key={c} value={c}>{CATEGORIA_LABEL[c] || c}</option>)}
                </select>
              </th>
            </tr>
          </thead>
          <tbody data-testid="compras-tabla">
            {lineas.length === 0 ? (
              <tr><td colSpan={10} className="text-center py-8 text-gray-400">
                {data.lineas.length ? "Sin coincidencias con los filtros" : "Todavía no hay órdenes de compra en esta obra"}
              </td></tr>
            ) : lineas.map(l => {
              const editada = Boolean(cambios[claveDe(l)]);
              return (
                <tr key={claveDe(l)} className={`border-t border-gray-50 ${editada ? "bg-blue-50/40" : ""}`} data-testid={`compras-fila-${l.oc_id}-${l.idx}`}>
                  <td className="px-3 py-1.5 font-mono text-[11px] text-gray-500 whitespace-nowrap" title={l.fecha}>{l.oc_number}</td>
                  <td className="px-3 py-1.5 text-gray-600 truncate max-w-[140px]" title={l.proveedor}>{l.proveedor}</td>
                  <td className="px-3 py-1.5 text-gray-800">{l.material} <span className="text-gray-400">({l.unidad})</span></td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{formatCantidad(l.cantidad)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-gray-500">{formatPYG(l.precio_unit)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums font-semibold">
                    {formatPYG(l.total_neto)}
                    {/* Fix FN (2026-10-09): la imputación la cierra la factura; sin factura, el monto de la OC */}
                    <span className={`block text-[9px] font-normal ${l.facturada ? "text-emerald-600" : "text-gray-400"}`} data-testid={`compras-origen-${l.oc_id}-${l.idx}`}>{l.facturada ? "según factura" : "según OC"}</span>
                  </td>
                  <td className="px-2 py-1">
                    <select value={valorDe(l, "item_uid")} onChange={e => editar(l, "item_uid", e.target.value)}
                      className="w-40 border border-gray-200 rounded px-1.5 py-1" data-testid={`compras-item-${l.oc_id}-${l.idx}`}>
                      <option value="">— Sin ítem —</option>
                      {(data.items_pto || []).map(i => <option key={i.item_uid} value={i.item_uid}>{i.numero} {i.descripcion}</option>)}
                    </select>
                  </td>
                  <td className="px-2 py-1">
                    <select value={valorDe(l, "nivel_id")} onChange={e => editar(l, "nivel_id", e.target.value)}
                      className="w-28 border border-gray-200 rounded px-1.5 py-1" data-testid={`compras-nivel-${l.oc_id}-${l.idx}`}>
                      <option value="">— Sin nivel —</option>
                      {(data.niveles || []).map(n => <option key={n.id} value={n.id}>{n.label}</option>)}
                    </select>
                  </td>
                  <td className="px-2 py-1">
                    <select value={valorDe(l, "tarea_numero") ?? ""} onChange={e => editar(l, "tarea_numero", e.target.value)}
                      className="w-32 border border-gray-200 rounded px-1.5 py-1" data-testid={`compras-tarea-${l.oc_id}-${l.idx}`}>
                      <option value="">— Sin tarea —</option>
                      {(data.tareas || []).map(t => <option key={t.numero} value={t.numero}>{t.numero} · {t.nombre}</option>)}
                    </select>
                  </td>
                  <td className="px-2 py-1">
                    <select value={valorDe(l, "categoria")} onChange={e => editar(l, "categoria", e.target.value)}
                      className="w-32 border border-gray-200 rounded px-1.5 py-1" data-testid={`compras-categoria-${l.oc_id}-${l.idx}`}>
                      <option value="">— Sin asignación —</option>
                      {(data.categorias || []).map(c => <option key={c} value={c}>{CATEGORIA_LABEL[c] || c}</option>)}
                    </select>
                  </td>
                </tr>
              );
            })}
          </tbody>
          {lineas.length > 0 && (
            <tfoot>
              <tr className="bg-gray-50 font-bold text-gray-800">
                <td colSpan={5} className="px-3 py-2 text-right">Total filtrado (sin IVA)</td>
                <td className="px-3 py-2 text-right tabular-nums" data-testid="compras-total-filtrado">{formatPYG(totalFiltrado)}</td>
                <td colSpan={4}></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
